import React, { useEffect, useMemo, useState } from "react";
import { X, Search, FileEdit, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../../utils/httpRequest";
import { toast } from "react-toastify";
import ConfirmModal from "../common/ConfirmModal";

export default function DraftsPickerModal({ isOpen, onClose, token, onPick }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exams, setExams] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    fetchDrafts();
  }, [isOpen]);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      // ساده: همه رو بگیر و سمت کلاینت فیلتر کن (isPublished=false یا status='draft')
      const res = await httpRequest(
        "GET",
        "/exam",
        {},
        {
          "x-access-token": token,
        }
      );
      const arr = res?.data?.data || res?.data || [];
      const drafts = arr.filter(
        (e) => e?.isPublished === false || e?.status === "draft"
      );
      setExams(drafts);
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
      setExams([]);
    } finally {
      setLoading(false);
    }
  };

  const askDelete = (examId) => {
    setDeleteTarget(examId);
  };

  const confirmDelete = async () => {
    try {
      await httpRequest(
        "DELETE",
        `/exam/${deleteTarget}`,
        {},
        { "x-access-token": token }
      );

      setExams((prev) => prev.filter((e) => e._id !== deleteTarget));
      toast.success(t("drafts_picker.deleted", "پیش‌نویس حذف شد"));
    } catch (err) {
      toast.error(err?.message || t("drafts_picker.delete_failed"));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleDeleteDraft = async (examId) => {
    const ok = window.confirm(
      t("drafts_picker.confirm_delete", "آیا از حذف این پیش‌نویس مطمئن هستید؟")
    );
    if (!ok) return;

    try {
      await httpRequest(
        "DELETE",
        `/exam/${examId}`,
        {},
        { "x-access-token": token }
      );
      // از استیت حذفش کن
      setExams((prev) => prev.filter((e) => e._id !== examId));
      toast.success(t("drafts_picker.deleted", "پیش‌نویس با موفقیت حذف شد."));
    } catch (err) {
      toast.error(
        err?.message ||
          t("drafts_picker.delete_failed", "حذف پیش‌نویس با خطا مواجه شد.")
      );
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return exams;
    return exams.filter((e) => {
      const fa = e?.title?.fa || e?.titleFa || "";
      const en = e?.title?.en || e?.titleEn || "";
      return fa.toLowerCase().includes(term) || en.toLowerCase().includes(term);
    });
  }, [q, exams]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl rounded-2xl shadow-xl p-4 md:p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold">
            {t("drafts_picker.title")}
          </h2>
          <button
            className="p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="close"
          >
            <X />
          </button>
        </div>

        {/* Search */}
        <div className="flex items-center border rounded-lg px-3 py-2 mb-3">
          <Search className="w-4 h-4 opacity-60" />
          <input
            className="flex-1 px-2 outline-none"
            placeholder={t("drafts_picker.search_ph")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button
            onClick={fetchDrafts}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {t("drafts_picker.refresh")}
          </button>
        </div>

        <div className="max-h-[55vh] overflow-auto border rounded-xl">
          {loading ? (
            <div className="p-4 text-sm text-gray-500">
              {t("drafts_picker.loading")}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">
              {t("drafts_picker.empty")}
            </div>
          ) : (
            <ul className="divide-y">
              {filtered.map((ex) => {
                const title =
                  ex?.title?.fa ||
                  ex?.titleFa ||
                  ex?.title?.en ||
                  ex?.titleEn ||
                  t("drafts_picker.no_title");
                const updated = ex?.updatedAt ? new Date(ex.updatedAt) : null;
                return (
                  <li key={ex._id} className="p-3 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {title}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t("drafts_picker.updated_at")}{" "}
                        {updated
                          ? updated.toLocaleString()
                          : t("drafts_picker.na")}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* دکمه حذف */}
                      <button
                        type="button"
                        onClick={() => askDelete(ex._id)}
                        className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1 text-xs md:text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t("drafts_picker.delete", "حذف")}
                      </button>

                      {/* دکمه ادامه / ویرایش */}
                      <button
                        onClick={() => onPick?.(ex)}
                        className="px-3 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2 text-xs md:text-sm"
                      >
                        <FileEdit className="w-4 h-4" />
                        {t("drafts_picker.continue")}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className={`mt-3 flex ${isRtl ? "justify-start" : "justify-end"}`}>
          <button
            className="px-4 py-2 rounded-lg border hover:bg-gray-50"
            onClick={onClose}
          >
            {t("common.close")}
          </button>
        </div>
      </div>
      <ConfirmModal
        open={!!deleteTarget}
        title={t("drafts_picker.confirm_title", "حذف پیش‌نویس")}
        message={t(
          "drafts_picker.confirm_delete",
          "آیا از حذف این پیش‌نویس مطمئن هستید؟"
        )}
        confirmText={t("common.delete", "حذف")}
        cancelText={t("common.cancel", "انصراف")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
